import {
  GoabAccordion,
  GoabButton,
  GoabDivider,
  GoabIcon,
  GoabText,
} from '@abgov/angular-components';
import '@abgov/web-components';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { waitForContent } from '@libs/common/ui/src/utils/wait-for-content';

@Component({
  selector: 'app-help',
  standalone: true,
  templateUrl: './help.component.html',
  styleUrl: './help.component.scss',
  imports: [
    CommonModule,
    GoabAccordion,
    GoabButton,
    GoabText,
    GoabDivider,
    GoabIcon,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HelpComponent implements AfterViewInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  facilityReportersRights = [
    'Log in using their registered email and password',
    'Record and submit weekly bed capacity',
    'View facility summaries (e.g. report due dates, submission status)',
    'Sort facility name, report due date, and report submitted date',
    'Use the “No changes this week” checkbox to quickly re-submit unchanged reports',
    'Contact application support if needed',
  ];

  sections = [
    {
      id: '1',
      title: 'Getting the numbers right: Operational and Occupied',
      sectionDisplayTitle:
        'Getting the numbers right: how to fill out the form',
      sections: [
        {
          id: '1-1',
          title:
            'Occupied beds: filled beds, matched beds and referral assessments.',
          sections: [],
          content: [
            'Include all beds currently occupied by a resident, as well as matched beds where an admission is pending or referral under assessment.',
            'These beds are not available for referral but would be if a resident was discharged or a referral was not accepted.',
          ],
        },
        {
          id: '1-2',
          title:
            'Operational beds: beds open for referral and occupied filled beds, matched beds and referral assessments.',
          sections: [],
          content: [
            'Operational beds include all occupied beds, plus all beds vacant and open for new referrals. This includes short-term maintenance that does not limit referrals.',
            'Non-operational beds are beds that are empty but not eligible for referrals. Beds may be closed because of staffing issues, outbreak management, long-term maintenance that prevents availability, an emergency or other operational reason.',
            'Additionally, a bed would be non-operational if filled by a lodge resident when no ALA referral was made.',
            'Please choose the relevant reasons that appear if you have operational bed changes.',
          ],
        },
        {
          id: '1-3',
          title: 'Vacancy: beds open for new referrals ',
          sections: [],
          content: [
            'Should show the number of beds that are open for a referral from Assisted Living Alberta when your report. Vacancy is calculated from the difference between occupied and operational beds at the time of reporting.',
          ],
        },
        {
          id: '1-4',
          title: 'Funded beds',
          sections: [],
          content: [
            'The maximum number of beds on contract with Assisted Living Alberta.',
          ],
        },
      ],

      content: [],
    },
    {
      id: '2',
      title: 'Account changes and new user requests',
      sections: [
        {
          id: '2-1',
          title: 'Account changes in two business days',
          sections: [],
          content: [
            'Account changes and new accounts requested will be added within two business days.',
          ],
        },
        {
          id: '2-2',
          title: 'Up to five total accounts',
          sections: [],
          content: [
            'Each facility has an Owner/Operator contact and Accommodation contact. These official site contacts have accounts set up by default. Up to three additional contacts may be added to ensure coverage for capacity reporting. Please ensure that your team can report every week, even in the event of illness or during vacations.',
          ],
        },
      ],
      content: [],
    },
    {
      id: '3',
      title: 'Frequently asked questions',
      sections: [
        {
          id: '3-1',
          title: 'About the application',
          sections: [
            {
              id: '3-1-1',
              title:
                'What is the Assisted Living Capacity Reporting and Monitoring application?',
              content: [
                'It is a digital platform used to track and monitor bed availability across continuing care type A, B, and C licensed spaces in Alberta. It helps to inform operational planning, surge response, and resource allocation.',
              ],
            },
            {
              id: '3-1-2',
              title: 'Who uses this application?',
              content: [
                'Facility Reporters submit weekly bed capacity reports, while Program Coordinators oversee the reporting process to ensure accuracy and timeliness. Government leaders and program areas use the capacity reporting dashboard to view data from across the province.',
              ],
            },
          ],
        },
        {
          id: '3-2',
          title: 'Roles, accounts and facility assignments',
          sections: [
            {
              id: '3-2-1',
              title: 'Who is a Facility Reporter?',
              content: [
                'A Facility Reporter is an individual designated by a facility to submit weekly bed capacity reports on behalf of the facility.',
              ],
            },
            {
              id: '3-2-2',
              title:
                'Which facilities are assigned to individual facility reporters?',
              content: [
                'Facility Reporters may be assigned to more than one facility. The designated Owner/Operator and Accommodation contacts are automatically assigned as Facility Reporters for their respective sites.',
                'When requesting an additional user account, please include the name and address of each assigned facility in the template provided above.',
                'To change the facility assignments for an existing reporter, submit the same template as an account update.',
              ],
            },
            {
              id: '3-2-3',
              title: 'What can Facility Reporters do in the application?',
              content: [
                `<ul class="reporter-guidance">${this.facilityReportersRights
                  .map((right) => `<li>${right}</li>`)
                  .join('')}</ul>`,
              ],
            },
            {
              id: '3-2-4',
              title:
                'How does one get added or removed from the Facility reporter role?',
              content: [
                'Please refer to the section above on account requests and changes. You must complete the template from this help page and email it to the Capacity Reporting team at the email address below. Owner/Operator and Accommodations contacts for each facility are reporters for their facilities and may request additional reporters to assist.',
              ],
            },
            {
              id: '3-2-5',
              title:
                'What if someone leaves their position and is no longer doing capacity reporting?',
              content: [
                "Please inform the Capacity Reporting team at the email address below to remove any Facility Reporters who are no longer working in that role. Please share the individual's name, email address, and facility name and address.",
              ],
            },
          ],
        },
        {
          id: '3-3',
          title: 'Reporting requirements and deadlines',
          sections: [
            {
              id: '3-3-1',
              title: 'How often do I need to submit reports?',
              content: [
                'Reports are due every week for your assigned facility. The reporting period is from Tuesday through to Monday. Reports are due on Tuesday at 4:59 pm. If Tuesday is an Alberta holiday, reports are due the next business day at 4:59 pm.',
              ],
            },
            {
              id: '3-3-2',
              title:
                'I don’t see any reports to complete when I log in. What should I do?',
              content: [
                'Congratulations, you are all caught up. New reports to complete are available on Mondays, the last day of the weekly reporting period. Reports are due on Tuesday.',
              ],
            },
            {
              id: '3-3-3',
              title: 'When can I submit reports?',
              content: [
                'Reports may be submitted when they are available in the reporting system, generally on Mondays. Reports are due on Tuesday at 4:59 pm. If Tuesday is an Alberta holiday, reports are due the next business day at 4:59 pm.',
              ],
            },
            {
              id: '3-3-4',
              title: 'What happens if I miss a report deadline?',
              content: [
                'Program Coordinators may follow up to ensure timely submission.',
              ],
            },
          ],
        },
        {
          id: '3-4',
          title: 'Using the application',
          sections: [
            {
              id: '3-4-1',
              title:
                'If I start an entry, can I leave it and come back to it later?',
              sections: [],
              content: [
                'Yes – When you start an entry, the status of the report is changed from Not Started to In progress. Use the Complete this entry later button. If you close the entry at that stage, it will be saved, and you can pick it up from where you left off.',
              ],
            },
            {
              id: '3-4-2',
              title:
                'There are numbers already in my reports when I open them. What’s going on?',
              sections: [],
              content: [
                'The numbers from the previous week are carried over. For smaller facilities the numbers may not change week-over-week. If the numbers are the same, please choose the “No changes” checkbox and submit the report. Otherwise enter the numbers for each facility.',
              ],
            },
            {
              id: '3-4-3',
              title:
                'I don’t see any reports to complete when I log in. What should I do?',
              sections: [],
              content: [
                'Congratulations, you are all caught up. New reports to complete are available on Mondays, the last day of the weekly reporting period. Reports are due on Tuesday.',
              ],
            },
            {
              id: '3-4-4',
              title:
                'I missed a couple weeks and now I can’t click on some of the report links. How do I fix this?',
              sections: [],
              content: [
                'Reports need to be completed in order, from oldest to newest. Complete the oldest report to unlock the next reporting period. Use the Not Started tab to sort reports from oldest to newest to make this catch up easier. Please request additional accounts for other Facility Reporters to ensure coverage during holidays or illness.',
              ],
            },
            {
              id: '3-4-5',
              title:
                'If I miss submitting a previous report, can I simply go ahead and submit the most recent one?',
              sections: [],
              content: [
                'No – You will be required to submit all previous reports first. Newer reports will be disabled until all older reports have been submitted.',
              ],
            },
          ],
          content: [],
        },
        {
          id: '3-5',
          title: 'Access and login',
          sections: [
            {
              id: '3-5-1',
              title:
                'If a colleague is not sure how to log in, what should I tell them?',
              sections: [],
              content: [
                'Navigate to www.alacapacity.alberta.ca and use your individual credentials – registered email address and password to access the application. When you first login, please copy and paste your temporary password exactly.',
              ],
            },
            {
              id: '3-5-2',
              title:
                'What should I tell my colleague if they forget their password or can’t log in?',
              sections: [],
              content: [
                'Use the “Forgot Password?” link on the Sign in page to reset your password. This will email a link to reset your password to the email account on file. If you still cannot log in after a password reset please email the Capacity Reporting team.',
              ],
            },
          ],
          content: [],
        },
      ],
      content: [],
    },
    {
      id: '4',
      title: 'Smoking policy',
      // some content here is coded in the template directly, because of its complexity, for now.
      // the main difficulty is incorporating the Design Alberta components in a variable
      // assigned to [innerHTML] or otherwise bypassing Angular's regular component creation.
      sections: [
        {
          id: '4-1',
          title: '',
          sections: [],
          content: [
            'Use this section to confirm whether your facility has a written smoking policy in place.',
          ],
        },
        {
          id: '4-2',
          title: 'What to select each week',
          sections: [],
          content: [
            'Yes, we have a documented smoking policy: Select this option if your facility has a smoking policy in place. The system will record your confirmation.',
            'No, we don’t have a smoking policy, but we are creating one: Select this option if your facility does not yet have a smoking policy. You should continue selecting No each week until a smoking policy is in place for your facility.',
          ],
        },
        {
          id: '4-3',
          title: 'Changing a previous response',
          sections: [],
          content: [],
        },
      ],
      content: [],
    },
    {
      id: '5',
      title: 'Email the support team',
      // some content here is coded in the template directly, because of its complexity, for now.
      // the main difficulty is incorporating the Design Alberta components in a variable
      // assigned to [innerHTML] or otherwise bypassing Angular's regular component creation.
      sections: [
        {
          id: '5-1',
          title: 'Timelines for responses',
          sections: [],
          content: [
            'Email will be responded to within one to two business days. Account changes or requests require two business days to complete.',
          ],
        },
        {
          id: '5-2',
          title: 'About requests to change facility details',
          sections: [],
          content: [
            'Changes to facility details (i.e., changes in the number of funded spaces, change to official site contact, facility name etc.) will be referred to Assisted Living and Social Services Continuing Care Licensing Office to approve any changes through standard processes.',
            'You may be asked to submit additional documentation to the Continuing Care Licensing Office depending on the nature of the inquiry. These may include an updated Notice to Director form or a Duty to Notify form. The Continuing Care Licensing Office will follow up on these inquiries through their usual processes.',
          ],
        },
      ],
      content: [],
    },
  ];

  toc = [
    this.sections[0].title,
    this.sections[1].title,
    this.sections[2].title,
    this.sections[3].title,
    this.sections[4].title,
  ];

  helpRequestDetailPrompts = [
    'Your name',
    'Facility name',
    'Facility address',
    'Describe your issue',
  ];

  emailHref = `mailto:ALSS.alacapacity@gov.ab.ca?subject=Capacity%20reporting%20request&body=${this.helpRequestEmailPrompts}`;

  get helpRequestEmailPrompts() {
    const promptTail = '%3A%20';
    const eol = '%0D%0A%0D%0A';
    return `${this.helpRequestDetailPrompts
      .join(`${promptTail}${eol}`)
      .replace(/ /g, '%20')}${promptTail}`;
  }

  ngAfterViewInit(): void {
    this.route.fragment.subscribe((fragment) => {
      if (fragment) {
        setTimeout(() => {
          if (fragment === 'smokingPolicySection')
            this.scrollTo('help-section-4-title');
        }, 0);
      }
    });
    waitForContent('goa-text', this.onContentVisible.bind(this));
  }
  private onContentVisible(goaTexts: HTMLElement[]) {
    this.replaceMarkups(goaTexts);
    const toc = this.getShadowParagraphs(document.querySelector('.help-toc'));
    const supportHints = this.getShadowParagraphs(
      document.querySelector('.help-request-hint')
    );
    this.updateListItemTextColors(toc);
    this.updateListItemMargins(toc);
    this.updateListItemMargins(supportHints);
    this.indentElement(
      document.querySelector('ul.reporter-guidance') as HTMLElement
    );
    this.indentElement(
      document.querySelector('ol.help-request-hint') as HTMLElement
    );
  }

  private indentElement(element: HTMLElement | null = null, x = '1rem') {
    if (element) {
      element.style.transform = `translateX(${x})`;
    }
  }

  private getShadowParagraphs(host: Element | null): HTMLElement[] {
    const paragraphs: HTMLElement[] = [];
    if (host) {
      const goaTexts = host.querySelectorAll('goa-text');
      goaTexts.forEach((goaText) => {
        const shadowRoot = goaText.shadowRoot;
        if (shadowRoot) {
          const p: HTMLElement | null = shadowRoot.querySelector('p');
          if (p) {
            paragraphs.push(p);
          }
        }
      });
    }
    return paragraphs;
  }

  private updateListItemTextColors(list: HTMLElement[]) {
    list.forEach((p) => {
      p.style.color = 'inherit';
    });
  }

  private updateListItemMargins(list: HTMLElement[]) {
    list.forEach((p) => {
      p.style.marginTop = '0';
      p.style.marginBottom = '0.5rem';
    });
  }

  private replaceMarkups(goaTexts: HTMLElement[]) {
    function isHtmlFragment(str: string): boolean {
      const parser = new DOMParser();
      const doc = parser.parseFromString(str, 'text/html');
      return doc.body.childNodes.length > 0;
    }
    goaTexts.forEach((goaText) => {
      const shadowRoot = goaText.shadowRoot;
      if (shadowRoot) {
        const text =
          (shadowRoot.querySelector('p') as HTMLElement)
            ?.querySelector('slot')
            ?.assignedNodes({ flatten: true })
            ?.find((node) => node.nodeType === Node.TEXT_NODE)?.textContent ??
          '';
        if (isHtmlFragment(text)) {
          goaText.innerHTML = text;
        }
      }
    });
  }

  goBack() {
    this.router.navigate(['/view-entries']);
  }

  expandedList: boolean[] = new Array(this.sections[2].sections.length).fill(
    false
  );
  expandedAll = false;
  accordionStatus = 'Show all sections';

  toggleAccordion(index: number, open: boolean): void {
    this.expandedList[index] = open;
    this.updateAccordionStatus();
  }

  onToggleAccordion(): void {
    const isExpanding = this.expandedList.some((isOpen) => !isOpen);
    this.expandedList = this.expandedList.map(() => isExpanding);
    this.updateAccordionStatus();
  }

  private updateAccordionStatus(): void {
    this.expandedAll = this.expandedList.every((isOpen) => isOpen);
    this.accordionStatus = this.expandedList.every((isOpen) => isOpen)
      ? 'Hide all sections'
      : 'Show all sections';
  }

  isHtmlFragment(str: string): boolean {
    if (!str) return false;

    const parser = new DOMParser();
    const html = parser.parseFromString(str, 'text/html');
    return html.body.children.length > 0;
  }

  scrollTo(elementId: string) {
    const element = document.getElementById(elementId) as HTMLElement;
    console.log(element);
    if (element) {
      setTimeout(() => {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }, 10);
    }
  }
}
